import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '../../contexts/AuthContext'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Button from '../ui/Button'

const { FiX, FiUser } = FiIcons

const AddChildModal = ({ isOpen, onClose, onAddChild }) => {
  const { family } = useAuth()
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    avatar: '👦'
  })

  const avatarOptions = [
    // Basic children - various ethnicities
    '👦', '👧', '🧒', '👶',
    
    // Light skin tone
    '👦🏻', '👧🏻', '🧒🏻', '👶🏻',
    
    // Medium-light skin tone
    '👦🏼', '👧🏼', '🧒🏼', '👶🏼',
    
    // Medium skin tone
    '👦🏽', '👧🏽', '🧒🏽', '👶🏽',
    
    // Medium-dark skin tone
    '👦🏾', '👧🏾', '🧒🏾', '👶🏾',
    
    // Dark skin tone
    '👦🏿', '👧🏿', '🧒🏿', '👶🏿',

    // Superheroes - Male (various skin tones)
    '🦸‍♂️', '🦸🏻‍♂️', '🦸🏼‍♂️', '🦸🏽‍♂️', '🦸🏾‍♂️', '🦸🏿‍♂️',
    
    // Superheroes - Female (various skin tones)
    '🦸‍♀️', '🦸🏻‍♀️', '🦸🏼‍♀️', '🦸🏽‍♀️', '🦸🏾‍♀️', '🦸🏿‍♀️',

    // Supervillains - Male (various skin tones)
    '🦹‍♂️', '🦹🏻‍♂️', '🦹🏼‍♂️', '🦹🏽‍♂️', '🦹🏾‍♂️', '🦹🏿‍♂️',
    
    // Supervillains - Female (various skin tones)
    '🦹‍♀️', '🦹🏻‍♀️', '🦹🏼‍♀️', '🦹🏽‍♀️', '🦹🏾‍♀️', '🦹🏿‍♀️',

    // Royalty - various skin tones
    '🤴', '🤴🏻', '🤴🏼', '🤴🏽', '🤴🏾', '🤴🏿',
    '👸', '👸🏻', '👸🏼', '👸🏽', '👸🏾', '👸🏿',

    // Mages/Wizards - various skin tones
    '🧙‍♂️', '🧙🏻‍♂️', '🧙🏼‍♂️', '🧙🏽‍♂️', '🧙🏾‍♂️', '🧙🏿‍♂️',
    '🧙‍♀️', '🧙🏻‍♀️', '🧙🏼‍♀️', '🧙🏽‍♀️', '🧙🏾‍♀️', '🧙🏿‍♀️',

    // Genies - various skin tones
    '🧞‍♂️', '🧞‍♀️',

    // Elves - various skin tones
    '🧝‍♂️', '🧝🏻‍♂️', '🧝🏼‍♂️', '🧝🏽‍♂️', '🧝🏾‍♂️', '🧝🏿‍♂️',
    '🧝‍♀️', '🧝🏻‍♀️', '🧝🏼‍♀️', '🧝🏽‍♀️', '🧝🏾‍♀️', '🧝🏿‍♀️',

    // Fairies - various skin tones
    '🧚‍♂️', '🧚🏻‍♂️', '🧚🏼‍♂️', '🧚🏽‍♂️', '🧚🏾‍♂️', '🧚🏿‍♂️',
    '🧚‍♀️', '🧚🏻‍♀️', '🧚🏼‍♀️', '🧚🏽‍♀️', '🧚🏾‍♀️', '🧚🏿‍♀️',

    // Vampires - various skin tones
    '🧛‍♂️', '🧛🏻‍♂️', '🧛🏼‍♂️', '🧛🏽‍♂️', '🧛🏾‍♂️', '🧛🏿‍♂️',
    '🧛‍♀️', '🧛🏻‍♀️', '🧛🏼‍♀️', '🧛🏽‍♀️', '🧛🏾‍♀️', '🧛🏿‍♀️',

    // Fun characters and animals
    '🦄', '🌟', '🚀', '⭐', '🎨', '🎵', '📚', '⚽', '🏀', '⚾', '🎮', '🎪',
    '🐱', '🐶', '🐻', '🐸', '🦊', '🐰', '🐼', '🐯', '🦁', '🐨', '🐵', '🐧'
  ]

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    const childData = {
      ...formData,
      age: formData.age ? parseInt(formData.age) : null,
      family_id: family.id
    }

    const success = await onAddChild(childData)
    setLoading(false)

    if (success) {
      onClose()
      setFormData({ name: '', age: '', avatar: '👦' })
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Add New Child</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  className="p-2"
                >
                  <SafeIcon icon={FiX} className="text-xl" />
                </Button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Child's Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter child's name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Age (Optional)
                  </label>
                  <input
                    type="number"
                    name="age"
                    value={formData.age}
                    onChange={handleChange}
                    min="1"
                    max="18"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter age"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Choose Avatar
                  </label>
                  <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3">
                    <div className="grid grid-cols-6 gap-2">
                      {avatarOptions.map((avatar, index) => (
                        <button
                          key={`${avatar}-${index}`}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, avatar }))}
                          className={`
                            p-3 rounded-lg border-2 transition-all duration-200 text-2xl hover:scale-110
                            ${formData.avatar === avatar
                              ? 'border-primary-500 bg-primary-50 shadow-md'
                              : 'border-gray-200 hover:border-gray-300'
                            }
                          `}
                        >
                          {avatar}
                        </button>
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Scroll to see more avatar options including diverse superheroes, royalty, and magical characters
                  </p>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={onClose}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    loading={loading}
                    className="flex-1"
                  >
                    <SafeIcon icon={FiUser} className="text-sm" />
                    Add Child
                  </Button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}

export default AddChildModal